import React from "react";
import { connect } from "react-redux";
import { Route, Redirect, Switch, withRouter } from "react-router";
import PropTypes from "prop-types";
import { subscribe } from "MODULES/Upgrade/actions";
import {
  Search as CompanySearch,
  Detail as CompanyDetail
} from "MODULES/companies/components";
import {
  Search as PartsSearch,
  Detail as PartDetail,
  IncomingQuoteLineItems,
  OutgoingQuoteLineItems,
  ManagedRepairOrderLineItems,
  PurchaseOrderLineItems,
  RepairOrderLineItems,
  RepairQuoteLineItems,
  SalesOrderLineItems,
  Inventories
} from "MODULES/parts/components";
import {
  Search as UserSearch,
  Detail as UserDetail
} from "MODULES/users/components";
import {
  ADMIN,
  QUALITY_CONTROL,
  ACCOUNTING,
  SALES,
  REPAIR,
  WAREHOUSE,
  WAREHOUSE_BASIC,
  USER,
  EMAIL,
  FULL_NAME,
  ROLE,
  QUALITY_CONTROL_MANAGER,
  QUALITY_CONTROL_ADMIN,
  RECEIVING,
  EMAIL_VERIFIED
} from "MODULES/users/constants";
import ApprovedVendorSearch from "MODULES/ApprovedVendors/Search/Search";
import ApprovedRepairStationSearch from "MODULES/ApprovedRepairStations/Search/Search";
import Commissions from "MODULES/Commissions/components/Search/Search";
import MistakeShipmentsDetail from "MODULES/MistakeShipments/components/Detail/Detail";
import ConsignmentsDetail from "MODULES/Consignments/Detail/Detail";
import PurchaseOrders from "MODULES/PurchaseOrders/components/Search/Search";
import PurchaseOrderDetail from "MODULES/PurchaseOrders/components/Detail/Detail";
import RejectedCoreSearch from "MODULES/RejectedCores/components/Search/RejectedCoreSearch";
import RejectedCoreOrderDetail from "MODULES/RejectedCores/components/Detail/Detail";
import RepairOrderSearch from "MODULES/RepairOrders/components/Search/RepairOrderSearch";
import RepairOrderDetail from "MODULES/RepairOrders/components/Detail/Detail";
import IncomingQuotes from "MODULES/IncomingQuotes/Search/IncomingQuotes";
import IncomingQuoteDetail from "MODULES/IncomingQuotes/Detail/IncomingQuoteDetail";
import {
  Search as InventorySearch,
  Detail as InventoryDetail
} from "MODULES/inventories/components";
import ManagedRepairOrderDetail from "MODULES/ManagedRepairOrders/Detail/Detail";
import OutgoingQuotes from "MODULES/OutgoingQuotes/Search/OutgoingQuotes";
import OutgoingQuoteDetail from "MODULES/OutgoingQuotes/Detail/OutgoingQuoteDetail";
import ReceivingDashboard from "MODULES/Receiving/Dashboard";
import ReceivingDetail from "MODULES/Receiving/Detail";
import ReceivingSearch from "MODULES/Receiving/Search/ReceivingSearch";
import ReservedForRepairList from "MODULES/RepairOrders/components/ReserveForRepair";
import ReservedForSaleList from "MODULES/SalesOrders/ReserveForSale/ReservedForSaleList";
import ShippingDashboard from "MODULES/Shipping/components/Dashboard";
import ShoppingListSearch from "MODULES/MyShoppingList/Search/Search";
import CustomerRMASearch from "MODULES/CustomerRmas/components";

import { Detail as RMADetail } from "MODULES/RMAsAndWarranties/components";
import {
  Search as OutgoingShipmentSearch,
  Detail as OutgoingShipmentDetail
} from "MODULES/OutgoingShipments/components";
import {
  Search as SearchSalesOrders,
  Detail as SalesOrderDetail
} from "MODULES/SalesOrders/components";
import { Authorized, ErrorBoundary } from "MODULES/shared/components";
import { WithCurrentUser } from "MODULES/session/decorators/withCurrentUser";
import { ID, UUID } from "APP_ROOT/constants";
import PurchasesReport from "MODULES/PurchasesReport/Components/Search/Search";
import LotsSearch from "MODULES/Lots/Search/Lots";
import ResidentExpertSearch from "MODULES/MyShoppingList/ResidentExpertSearch/ResidentExpertSearch";
import ManagedRepairOrderSearch from "MODULES/ManagedRepairOrders/Search/ManagedRepairOrderSearch";
import LotDetail from "MODULES/Lots/Detail/Detail";
import { Search as VendorRMASearch } from "MODULES/VendorRmas/components";
import LotLineItems from "MODULES/parts/components/LotLineItems/LotLineItems";
import ConsignmentsSearch from "MODULES/Consignments/Search/Consignments";
import SalesReport from "MODULES/SalesDashboard/UserStats/SalesReport";
import Home from "./Home";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import { Table as DepreciationsDashboard } from "./modules/DepreciationsDashboard/Components/Table";
import PurchasesDashboard from "./modules/PurchasesDashboard/Components/Table";
import { Table as SalesDashboard } from "./modules/SalesDashboard/Components/Table";
import Forbidden from "./modules/shared/components/Authorized/Forbidden";
import MistakeShipmentSearch from "./modules/MistakeShipments/components/Search/MistakeShipmentSearch";
import HighScores from "./modules/HighScores/components/Report";

const includesName = actualPathName => pathName =>
  actualPathName.includes(pathName);

const navName = pathName => {
  const includesPath = includesName(pathName);

  switch (pathName) {
    case "/dashboard/receiving":
      return "Inbound Orders";
    case "/dashboard/shipping":
      return "Outbound Orders";
    case "/dashboard/virtual-receiving":
      return "Virtual Inbound Orders";
    default:
      break;
  }

  if (includesPath("part")) {
    return "Parts";
  } else if (includesPath("reports")) {
    return "Reports";
  } else if (includesPath("incoming-quotes")) {
    return "Incoming Quotes";
  } else if (includesPath("outgoing-quotes")) {
    return "Outgoing Quotes";
  } else if (includesPath("companies")) {
    return "Companies";
  } else if (includesPath("mistake-shipments")) {
    return "Mistake Shipments";
  } else if (includesPath("purchase-orders")) {
    return "Purchase Orders";
  } else if (includesPath("rejected-cores")) {
    return "Rejected Cores";
  } else if (includesPath("managed-repair-orders")) {
    return "Managed Repair Orders";
  } else if (includesPath("repair-orders")) {
    return "Repair Orders";
  } else if (includesPath("customer-rmas")) {
    return "Customer RMAs";
  } else if (includesPath("vendor-rmas")) {
    return "Vendor RMAs";
  } else if (includesPath("receiving")) {
    return "Received Orders";
  } else if (includesPath("shipping")) {
    return "Shipped Orders";
  } else if (includesPath("inventories")) {
    return "Inventories";
  } else if (includesPath("sales-orders")) {
    return "Sales Orders";
  } else if (includesPath("users")) {
    return "Users";
  }

  return "Dashboard";
};

export class Layout extends React.Component {
  componentDidMount() {
    this.props.subscribeToUpgradeNotifications();
    this.initializeRollbarForUser();
    if (this.props.currentUser && !this.props.currentUser.get(EMAIL_VERIFIED)) {
      document.body.classList.add("email-warning");
    }

    if (["staging", "development"].includes(process.env.NODE_ENV)) {
      document.body.classList.add("development");
    }
  }

  componentWillUnmount() {
    document.body.classList.remove("development");
    document.body.classList.remove("email-warning");
  }

  initializeRollbarForUser() {
    const { currentUser } = this.props;

    if (process.env.ROLLBAR_CLIENT_ACCESS_TOKEN) {
      try {
        // eslint-disable-next-line
        Rollbar.configure({
          payload: {
            person: {
              id: currentUser.get(ID),
              username: currentUser.get(FULL_NAME),
              email: currentUser.get(EMAIL)
            }
          }
        });
      } catch (ex) {
        // eslint-disable-next-line
        console.log("Unable to initialize Rollbar for user");
      }
    }
  }

  render() {
    const { currentUser, location } = this.props;
    const isAdmin = currentUser.get(ROLE) === ADMIN;
    const currentUserIdMatches = route =>
      route.match.params.id === currentUser.get(UUID);

    return (
      <div className="layout">
        <Nav
          header={navName(location.pathname)}
          currentUser={this.props.currentUser}
        />
        <div className="container-fluid">
          <Sidebar />
          <div className="main">
            <ErrorBoundary>
              <Route exact path="/" component={Home} />
              <Route exact path="/accounting-logs" component={Home} />
              <Route exact path="/company-ranking-stats" component={Home} />
              <Route exact path="/remaining-inventory" component={Home} />
              <Route exact path="/ranking-stats" component={Home} />
              <Route exact path="/api-keys" component={Home} />
              <Route exact path="/consignments-report" component={Home} />
              <Route exact path="/integrations" component={Home} />
              <Route exact path="/reports" component={Home} />
              <Route path="/consignments/:id" component={ConsignmentsDetail} />
              <Route
                exact
                path="/consignments"
                component={ConsignmentsSearch}
              />
              <Route
                exact
                path="/resident-expert-parts"
                component={ResidentExpertSearch}
              />
              <Route
                path="/dashboard/receiving"
                component={ReceivingDashboard}
              />
              <Route path="/dashboard/shipping" component={ShippingDashboard} />
              <Forbidden roles={[WAREHOUSE]}>
                <Route path="/monthly-sales" component={SalesDashboard} />
                <Route
                  path="/monthly-purchases"
                  component={PurchasesDashboard}
                />
              </Forbidden>
              <Authorized roles={[SALES]}>
                <Route path="/purchases-report" component={PurchasesReport} />
              </Authorized>
              <Authorized roles={[SALES]}>
                <Route path="/sales-report" component={SalesReport} />
              </Authorized>
              <Authorized
                roles={[SALES, REPAIR, ACCOUNTING]}
                subroles={[QUALITY_CONTROL_MANAGER, QUALITY_CONTROL_ADMIN]}
              >
                <Route
                  path="/monthly-depreciations"
                  component={DepreciationsDashboard}
                />
              </Authorized>
              <Authorized
                roles={[QUALITY_CONTROL, WAREHOUSE, REPAIR]}
                permissions={[RECEIVING]}
              >
                <Route
                  path="/dashboard/virtual-receiving"
                  render={() => <ReceivingDashboard virtual />}
                />
              </Authorized>
              <Route exact path="/incoming-quotes" component={IncomingQuotes} />
              <Route
                path="/incoming-quotes/:id"
                component={IncomingQuoteDetail}
              />
              <Authorized
                roles={[
                  ADMIN,
                  ACCOUNTING,
                  QUALITY_CONTROL,
                  REPAIR,
                  SALES,
                  WAREHOUSE
                ]}
              >
                <Forbidden subroles={[WAREHOUSE_BASIC]}>
                  <Route
                    exact
                    path="/inventories"
                    component={InventorySearch}
                  />
                </Forbidden>
              </Authorized>
              <Route
                path="/inventories/:id/:all?"
                component={InventoryDetail}
              />
              <Route exact path="/outgoing-quotes" component={OutgoingQuotes} />
              <Route
                path="/outgoing-quotes/:id"
                component={OutgoingQuoteDetail}
              />
              <Route exact path="/purchase-orders" component={PurchaseOrders} />
              <Route
                path="/purchase-orders/:id"
                component={PurchaseOrderDetail}
              />
              <Route exact path="/companies" component={CompanySearch} />
              <Route path="/companies/:id" component={CompanyDetail} />
              <Route
                exact
                path="/shipping"
                component={OutgoingShipmentSearch}
              />
              <Route path="/shipping/:id" component={OutgoingShipmentDetail} />
              <Route exact path="/lots" component={LotsSearch} />
              <Route exact path="/lots/:id" component={LotDetail} />
              <Route exact path="/parts" component={PartsSearch} />
              <Route exact path="/parts/:id" component={PartDetail} />
              <Route
                exact
                path="/managed-repair-orders"
                component={ManagedRepairOrderSearch}
              />
              <Route
                exact
                path="/repair-orders"
                component={RepairOrderSearch}
              />
              <Route
                exact
                path="/reports/commissions"
                component={Commissions}
              />
              <Route exact path="/reports/high-scores" component={HighScores} />
              <Route exact path="/vendor-rmas" component={VendorRMASearch} />
              <Route
                exact
                path="/my-shopping-list"
                component={ShoppingListSearch}
              />
              <Route
                exact
                path="/customer-rmas"
                component={CustomerRMASearch}
              />
              <Route
                exact
                path="/mistake-shipments"
                component={MistakeShipmentSearch}
              />
              <Route
                path={`/mistake-shipments/:id`}
                component={MistakeShipmentsDetail}
              />
              <Route
                exact
                path="/rejected-cores"
                component={RejectedCoreSearch}
              />
              <Route
                path="/rejected-cores/:id"
                component={RejectedCoreOrderDetail}
              />
              <Route path="/rmas/:id" component={RMADetail} />
              <Route exact path="/sales-orders" component={SearchSalesOrders} />
              <Switch>
                <Route
                  exact
                  path="/sales-orders/reserved"
                  component={ReservedForSaleList}
                />
                <Route path="/sales-orders/:id" component={SalesOrderDetail} />
              </Switch>
              <Switch>
                <Route
                  exact
                  path="/repair-orders/reserved"
                  component={ReservedForRepairList}
                />
                <Route
                  path="/repair-orders/:id"
                  component={RepairOrderDetail}
                />
              </Switch>
              <Route
                exact
                path="/managed-repair-orders/:id"
                component={ManagedRepairOrderDetail}
              />
              <Route exact path="/receiving" component={ReceivingSearch} />
              <Route exact path="/receiving/:id" component={ReceivingDetail} />
              <Route path="/parts/:id/inventories" component={Inventories} />
              <Route
                path="/parts/:id/incoming-quote-line-items"
                component={IncomingQuoteLineItems}
              />
              <Route
                path="/parts/:id/outgoing-quote-line-items"
                component={OutgoingQuoteLineItems}
              />
              <Route
                path="/parts/:id/purchase-order-line-items"
                component={PurchaseOrderLineItems}
              />
              <Route
                path="/parts/:id/repair-order-line-items"
                component={RepairOrderLineItems}
              />
              <Route
                path="/parts/:id/lot-line-items"
                component={LotLineItems}
              />
              <Route
                path="/parts/:id/sales-order-line-items"
                component={SalesOrderLineItems}
              />
              <Route
                path="/parts/:id/managed-repair-order-line-items"
                component={ManagedRepairOrderLineItems}
              />
              <Route
                path="/parts/:id/repair-quote-line-items"
                component={RepairQuoteLineItems}
              />
              <Route
                exact
                path="/users"
                render={() => (isAdmin ? <UserSearch /> : <Redirect to="/" />)}
              />
              <Route
                path="/users/:id"
                render={route =>
                  currentUserIdMatches(route) || isAdmin ? (
                    <UserDetail {...route} />
                  ) : (
                    <Redirect to="/" />
                  )}
              />
              <Forbidden subroles={[WAREHOUSE_BASIC]}>
                <Route
                  exact
                  path="/approved-vendors"
                  component={ApprovedVendorSearch}
                />
                <Route
                  exact
                  path="/approved-repair-stations"
                  component={ApprovedRepairStationSearch}
                />
              </Forbidden>
            </ErrorBoundary>
          </div>
        </div>
      </div>
    );
  }
}

Layout.propTypes = {
  subscribeToUpgradeNotifications: PropTypes.func.isRequired,
  ...WithCurrentUser
};

export default connect(
  state => ({ currentUser: state.session.get(USER) }),
  dispatch => ({
    subscribeToUpgradeNotifications: () => dispatch(subscribe())
  })
)(withRouter(Layout));



// WEBPACK FOOTER //
// ./app/Layout.jsx